import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import designApi from "@services/designApi.jsx";
import "@assets/css/common/style_main.css";
import "@assets/css/common/style_page_design.css";
import "@assets/css/common/style_modal.css";
import "@assets/css/common/style_table.css";

// 썸네일 이미지 임포트 (실제 구현에서는 이미지 경로 조정 필요)
import startThumbnail from "@assets/images/thumbnails/start.png";
import angleThumbnail from "@assets/images/thumbnails/angle.png";
import layoutThumbnail from "@assets/images/thumbnails/layout.png";
import amountThumbnail from "@assets/images/thumbnails/amount.png";
import paymentThumbnail from "@assets/images/thumbnails/payment.png";
import paymentLoadingThumbnail from "@assets/images/thumbnails/payment_loading.png";
import shootingWaitThumbnail from "@assets/images/thumbnails/shooting_wait.png";
import shootingModeThumbnail from "@assets/images/thumbnails/shooting_mode.png";
import shootingLoadingThumbnail from "@assets/images/thumbnails/shooting_loading.png";
import selectModeThumbnail from "@assets/images/thumbnails/select_mode.png";
import printModeThumbnail from "@assets/images/thumbnails/print_mode.png";
import qrPageThumbnail from "@assets/images/thumbnails/qr_page.png";
import noticeThumbnail from "@assets/images/thumbnails/notice.png";

// 프리뷰 이미지 임포트 (실제 구현에서는 이미지 경로 조정 필요)
import startPreview from "@assets/images/preview/start_preview.png";
import anglePreview from "@assets/images/preview/angle_preview.png";
import layoutPreview from "@assets/images/preview/layout_preview.png";
import amountPreview from "@assets/images/preview/amount_preview.png";
import paymentPreview from "@assets/images/preview/payment_preview.png";
import paymentLoadingPreview from "@assets/images/preview/payment_loading_preview.png";
import shootingWaitPreview from "@assets/images/preview/shooting_wait_preview.png";
import shootingModePreview from "@assets/images/preview/shooting_mode_preview.png";
import shootingLoadingPreview from "@assets/images/preview/shooting_loading_preview.png";
import selectModePreview from "@assets/images/preview/select_mode_preview.png";
import printModePreview from "@assets/images/preview/print_mode_preview.png";
import qrPagePreview from "@assets/images/preview/qr_page_preview.png";
import noticePreview from "@assets/images/preview/notice_preview.png";

// 설정 페이지 컴포넌트 임포트
import StartSetting from "./components/StartSetting";
import AngleSetting from "./components/AngleSetting";
import LayoutSetting from "./components/LayoutSetting";
import AmountSetting from "./components/AmountSetting";
import PaymentSetting from "./components/PaymentSetting";
import PaymentLoadingSetting from "./components/PaymentLoadingSetting";
import CaptureStandbySetting from "./components/CaptureStandbySetting.jsx";
import CaptureSetting from "./components/CaptureSetting.jsx";
import PostCaptureLoadingSetting from "./components/PostCaptureLoadingSetting.jsx";
import PhotoSelectionModeSetting from "./components/PhotoSelectionModeSetting.jsx";
import PrintModeSetting from "./components/PrintModeSetting";
import QrPageSetting from "./components/QrPageSetting";
import ModalSetting from "./components/ModalSetting.jsx";

// 이미지 맵핑 객체
const thumbnailMap = {
    start: startThumbnail,
    angle: angleThumbnail,
    layout: layoutThumbnail,
    amount: amountThumbnail,
    payment: paymentThumbnail,
    payment_loading: paymentLoadingThumbnail,
    shooting_wait: shootingWaitThumbnail,
    shooting_mode: shootingModeThumbnail,
    shooting_loading: shootingLoadingThumbnail,
    select_mode: selectModeThumbnail,
    print_mode: printModeThumbnail,
    qr_page: qrPageThumbnail,
    notice: noticeThumbnail
};

const previewMap = {
    start: startPreview,
    angle: anglePreview,
    layout: layoutPreview,
    amount: amountPreview,
    payment: paymentPreview,
    payment_loading: paymentLoadingPreview,
    shooting_wait: shootingWaitPreview,
    shooting_mode: shootingModePreview,
    shooting_loading: shootingLoadingPreview,
    select_mode: selectModePreview,
    print_mode: printModePreview,
    qr_page: qrPagePreview,
    notice: noticePreview
};

const PageDesign = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const themeId = searchParams.get("themeId");

    // API 관련 상태 추가
    const [designPages, setDesignPages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // 중복 클릭 방어를 위한 상태 및 ref 추가
    const [isSaving, setIsSaving] = useState(false);
    const saveTimeoutRef = useRef(null);
    const lastSaveTimeRef = useRef(0);
    const saveCountRef = useRef(0);
    const saveInProgressRef = useRef(false);
    
    // 저장 방어 설정값들
    const SAVE_COOLDOWN = 1000; // 1초 내 중복 저장 방지
    const MAX_SAVE_ATTEMPTS = 3; // 연속 클릭 최대 허용 횟수
    const COOLDOWN_RESET_TIME = 5000; // 5초 후 카운터 리셋
    const DOUBLE_CLICK_PREVENTION = 200; // 더블클릭 방지 최소 간격 (200ms)

    const [currentTheme, setCurrentTheme] = useState({
        author: searchParams.get("author") || "cineticmotion24",
        theme: searchParams.get("theme") || "",
        date: searchParams.get("date") || "",
        permission: searchParams.get("permission") || "",
    });

    // 현재 선택된 페이지 정보
    const [currentPageInfo, setCurrentPageInfo] = useState({
        id: "",
        name: "",
        pageType: "start",
        previewResourceId : "",
        previewViewUrl: "",
        previewDownloadUrl: "",
        thumbnailResourceId: "",
        thumbnailViewUrl: "",
        thumbnailDownloadUrl: "",
        displayOrder: "",
        themeId: themeId,
    });

    // 기본 디자인 페이지 데이터
    const defaultDesignPages = [
        {
            pageType: "start",
            name: "시작화면",
            thumbnailViewUrl: startThumbnail,
            previewViewUrl: startPreview,
        },
        {
            pageType: "angle",
            name: "앵글선택",
            thumbnailViewUrl: angleThumbnail,
            previewViewUrl: anglePreview,
        },
        {
            pageType: "layout",
            name: "레이아웃",
            thumbnailViewUrl: layoutThumbnail,
            previewViewUrl: layoutPreview,
        },
        {
            pageType: "amount",
            name: "수량선택",
            thumbnailViewUrl: amountThumbnail,
            previewViewUrl: amountPreview,
        },
        {
            pageType: "payment",
            name: "결제",
            thumbnailViewUrl: paymentThumbnail,
            previewViewUrl: paymentPreview,
        },
        {
            pageType: "payment_loading",
            name: "결제 로딩",
            thumbnailViewUrl: paymentLoadingThumbnail,
            previewViewUrl: paymentLoadingPreview,
        },
        {
            pageType: "shooting_wait",
            name: "촬영대기",
            thumbnailViewUrl: shootingWaitThumbnail,
            previewViewUrl: shootingWaitPreview,
        },
        {
            pageType: "shooting_mode",
            name: "촬영모드",
            thumbnailViewUrl: shootingModeThumbnail,
            previewViewUrl: shootingModePreview,
        },
        {
            pageType: "shooting_loading",
            name: "촬영 후 로딩",
            thumbnailViewUrl: shootingLoadingThumbnail,
            previewViewUrl: shootingLoadingPreview,
        },
        {
            pageType: "select_mode",
            name: "선택모드",
            thumbnailViewUrl: selectModeThumbnail,
            previewViewUrl: selectModePreview,
        },
        {
            pageType: "print_mode",
            name: "인쇄모드/다운",
            thumbnailViewUrl: printModeThumbnail,
            previewViewUrl: printModePreview,
        },
        {
            pageType: "qr_page",
            name: "QR페이지",
            thumbnailViewUrl: qrPageThumbnail,
            previewViewUrl: qrPagePreview,
        },
        {
            pageType: "modal",
            name: "Modal",
            thumbnailViewUrl: noticeThumbnail,
            previewViewUrl: noticePreview,
        },
    ];

    // 컴포넌트 마운트 및 themeId 변경 시 API 호출
    useEffect(() => {
        // themeId가 없으면 API 호출 건너뛰기
        if (!themeId) {
            console.log('themeId가 없어 API 호출을 건너뜁니다.');
            return;
        }

        // API 호출 함수
        const fetchDesignPages = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const data = await designApi.getDesignPages(themeId);

                // API 데이터를 변환
                const transformedData = data.map(page => ({
                    id: page.id,
                    name: page.name,
                    pageType: page.pageType,
                    previewResourceId : page.previewResourceId,
                    previewViewUrl: page.previewViewUrl,
                    previewDownloadUrl: page.previewDownloadUrl,
                    thumbnailResourceId: page.thumbnailResourceId,
                    thumbnailViewUrl: page.thumbnailViewUrl,
                    thumbnailDownloadUrl: page.thumbnailDownloadUrl,
                    displayOrder: page.displayOrder,
                    themeId: page.themeId,
                }));

                setDesignPages(transformedData);
                setCurrentPageInfo(transformedData.find((page) => page.pageType === currentPageInfo.pageType) || defaultDesignPages[0]);
            } catch (err) {
                console.error('API 호출 실패:', err);
                setError(err);
                // 에러 발생 시 기본 데이터 유지
            } finally {
                setIsLoading(false);
            }
        };

        // API 호출 실행
        fetchDesignPages();
    }, [themeId]); // themeId가 변경될 때만 재실행

    // 컴포넌트 언마운트 시 타임아웃 정리
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // 페이지 변경 핸들러
    const handlePageChange = (pageType) => {
        setCurrentPageInfo(designPages.find((page) => page.pageType === pageType) || defaultDesignPages[0]);

        // URL 업데이트
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.set("page", pageType);
        navigate(
            {
                pathname: location.pathname,
                search: newSearchParams.toString(),
            },
            { replace: true }
        );
    };

    // 뒤로가기 핸들러
    const handleGoBack = () => {
        navigate("/design-management");
    };

    const [childrenComponent, setChildrenComponent] = useState(null);

    useEffect(() => {
        const renderComponent = renderSettings();
        setChildrenComponent( renderComponent );
    }, [currentPageInfo])

    // 설정 렌더링 함수
    const renderSettings = () => {
        if( currentPageInfo.themeId ) {
            switch (currentPageInfo.pageType) {
                case "start":
                    return <StartSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "angle":
                    return <AngleSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "layout":
                    return <LayoutSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "amount":
                    return <AmountSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "payment":
                    return <PaymentSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "payment_loading":
                    return <PaymentLoadingSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "shooting_wait":
                    return <CaptureStandbySetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "shooting_mode":
                    return <CaptureSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "shooting_loading":
                    return <PostCaptureLoadingSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "select_mode":
                    return <PhotoSelectionModeSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "print_mode":
                    return <PrintModeSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "qr_page":
                    return <QrPageSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                case "modal":
                    return <ModalSetting themeId={currentPageInfo.themeId} themePageMaster={currentPageInfo}/>;
                // 다른 페이지들도 여기에 추가 예정
                default:
                    return <div>해당 페이지의 설정이 준비 중입니다.</div>;
            }
        }
    };

    // 미리보기 버튼 핸들러
    const handlePreview = () => {
        alert("미리보기 기능이 준비 중입니다.");
    };

    // 중복 클릭 방어가 적용된 저장 함수
    const handleSave = useCallback(() => {
        console.log('=== 저장 요청 시작 ===');
        
        // 최초 가드: 진행 중 플래그 체크 (가장 빠른 방어)
        if (saveInProgressRef.current) {
            console.warn('❌ 이미 저장 진행 중 - 완전 차단');
            return;
        }
        
        // 진행 중 플래그 즉시 설정
        saveInProgressRef.current = true;
        
        try {
            // 1. 기본 유효성 검사
            if (!currentPageInfo.themeId) {
                alert('테마 정보가 없습니다.');
                return;
            }

            // 2. 현재 시간 체크
            const currentTime = Date.now();
            
            // 3. 이미 저장 중인 경우 방어
            if (isSaving) {
                console.warn('❌ 이미 저장 중입니다.');
                return;
            }
            
            // 4. 더블클릭 방지 체크
            const timeSinceLastSave = currentTime - lastSaveTimeRef.current;
            if (timeSinceLastSave < DOUBLE_CLICK_PREVENTION) {
                console.warn(`❌ 더블클릭 감지: ${timeSinceLastSave}ms < ${DOUBLE_CLICK_PREVENTION}ms`);
                return;
            }
            
            // 5. 연속 클릭 횟수 체크
            if (timeSinceLastSave < COOLDOWN_RESET_TIME) {
                saveCountRef.current += 1;
            } else {
                saveCountRef.current = 1; // 5초 이상 간격이면 카운트 리셋
            }
            
            if (saveCountRef.current > MAX_SAVE_ATTEMPTS) {
                alert('저장 요청이 너무 많습니다. 잠시 후 다시 시도해주세요.');
                return;
            }
            
            // 6. 저장 시작 시간 기록 및 상태 설정
            lastSaveTimeRef.current = currentTime;
            setIsSaving(true);
            
            console.log('✅ 저장 시작:', new Date().toISOString());
            
            // 기존 타임아웃 정리
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            
            // 7. 현재 페이지 타입에 맞는 이벤트 발생
            const eventName = `savePageDesign_${currentPageInfo.pageType}`;
            const saveEvent = new CustomEvent(eventName, {
                detail: { themeId: currentPageInfo.themeId }
            });
            document.dispatchEvent(saveEvent);
            
            console.log(`✅ 저장 이벤트 발송: ${eventName}`);
            
            // 저장 성공 시 카운터 리셋
            saveCountRef.current = 0;
            
        } catch (error) {
            console.error('❌ 저장 중 오류 발생:', error);
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            console.log('🔄 저장 상태 정리 시작');
            
            // 즉시 진행 중 플래그 해제
            saveInProgressRef.current = false;
            
            // 지연된 상태 해제 (최소 500ms 대기)
            saveTimeoutRef.current = setTimeout(() => {
                setIsSaving(false);
                console.log('✅ 저장 상태 해제 완료:', new Date().toISOString());
            }, 500);
        }
    }, [currentPageInfo.themeId, currentPageInfo.pageType, isSaving]);

    // 저장 버튼 클릭 이벤트 핸들러 (추가 방어층)
    const handleSaveButtonClick = useCallback((e) => {
        console.log('🔘 버튼 클릭 감지');
        
        // 이벤트 중복 방지
        e.preventDefault();
        e.stopPropagation();
        
        // 최고 우선순위 가드
        if (saveInProgressRef.current) {
            console.warn('❌ 이미 진행 중 - 버튼 클릭 무시');
            return;
        }
        
        // 버튼이 비활성화된 상태라면 클릭 무시
        if (e.target.disabled || isSaving) {
            console.warn('❌ 저장 버튼 비활성 또는 저장 중 상태');
            return;
        }
        
        // 마지막 방어선: 더블클릭 방지를 위한 추가 체크
        const now = Date.now();
        if (now - lastSaveTimeRef.current < DOUBLE_CLICK_PREVENTION) {
            console.warn(`❌ 더블클릭 감지 - 버튼에서 차단: ${now - lastSaveTimeRef.current}ms`);
            return;
        }
        
        console.log('✅ 저장 함수 호출');
        handleSave();
    }, [handleSave, isSaving]);

    // 로딩 중 표시
    if (isLoading && themeId) {
        return <div className="loading-container">로딩 중...</div>;
    }

    // 에러 표시 (콘솔에만 표시하고 하드코딩된 데이터 사용)
    if (error && themeId) {
        console.error("페이지 디자인 데이터 로드 오류:", error);
    }

    if (isLoading) {
        return <div className="loading-container">로딩 중...</div>;
    }

    return (
        <>
            {/* 로딩 오버레이 추가 */}
            {isSaving && (
                <div className="loading-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div className="loading" style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '8px'
                    }}>저장 중...</div>
                </div>
            )}

            <div className="page-title-container">
                <a
                    href="#"
                    className="back-button"
                    title="이전으로 돌아가기"
                    onClick={(e) => {
                        e.preventDefault();
                        handleGoBack();
                    }}
                >
                    <i className="ri-arrow-left-line"></i>
                </a>
                <div className="title-theme-container">
                    <h1 className="page-title">페이지 디자인</h1>
                    <h1 className="theme-name">
                        {currentTheme.theme ? `ㅣ ${currentTheme.theme}` : ""}
                    </h1>
                </div>
            </div>

            <div className="page-design-container">
                <h3 className="section-title">페이지 선택</h3>
                {/* 통합된 디자인 선택 컨테이너 */}
                <div className="design-selection-container">
                    {designPages.map((page) => (
                        <div
                            key={page.id}
                            className="design-item-container"
                            data-preview={page.previewViewUrl}
                            data-name={page.name}
                            data-page={page.id}
                            onClick={() => handlePageChange(page.pageType)}
                        >
                            {/* 디자인 탭 */}
                            <div className="design-tab">{page.name}</div>

                            {/* 썸네일 이미지 */}
                            <div className="thumbnail-item">
                                <img src={page.thumbnailViewUrl} alt={page.name} />
                            </div>

                            {/* 라디오 버튼 */}
                            <div className="tab-radio">
                                <input
                                    type="radio"
                                    name="tab-select"
                                    checked={currentPageInfo.pageType === page.pageType}
                                    onChange={() => {}}
                                />
                            </div>
                        </div>
                    ))}
                </div>

                <h3 className="section-title">디자인 프리뷰</h3>
                {/* 16:9 이미지 프리뷰 컨테이너 */}
                <div className="design-preview-container">
                    {currentPageInfo && currentPageInfo.previewViewUrl ? (
                        <img
                            src={currentPageInfo.previewViewUrl}
                            alt={currentPageInfo.name || "프리뷰 이미지"}
                            id="preview-image"
                        />
                    ) : (
                        <div className="no-preview">프리뷰 이미지가 없습니다</div>
                    )}
                </div>

                {/* 디자인 설정 패널 */}
                <div className="design-settings" id="design-settings">
                    {/*{renderSettings()}*/}
                    {childrenComponent}
                </div>

                {/* 저장 버튼 - 개선된 버전 */}
                <div className="common-button-container">
                    <button
                        className="design-preview-button"
                        onClick={handlePreview}
                    >
                        미리보기
                    </button>
                    <button 
                        className="common-button" 
                        onClick={handleSaveButtonClick}
                        disabled={isSaving}
                        onDoubleClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.warn('더블클릭 이벤트 차단');
                        }}
                        style={{
                            backgroundColor: isSaving ? '#ccc' : '',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            opacity: isSaving ? 0.6 : 1,
                            pointerEvents: isSaving ? 'none' : 'auto'
                        }}
                    >
                        {isSaving ? '저장 중...' : '저장하기'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default PageDesign;